import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "jackstone_token";
const KIND_KEY = "jackstone_token_kind"; // "user" | "employee"

export function AuthProvider({ children }) {
  // Tier-1 (company Google gate)
  const [companyAuthed, setCompanyAuthed] = useState(false);
  const [companyEmail, setCompanyEmail] = useState(null);
  // Tier-2 (internal app user). user: null=loading, false=logged out, object=record
  const [user, setUser] = useState(null);
  const [kind, setKind] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const s = await api.get("/auth/google/session");
        if (!active) return;
        const authed = !!s.data?.authenticated;
        setCompanyAuthed(authed);
        setCompanyEmail(s.data?.email || null);
        if (!authed) {
          setUser(false);
          return;
        }
        // Tier-1 OK → resolve tier-2 from the stored token, if any.
        const token = localStorage.getItem(TOKEN_KEY);
        const storedKind = localStorage.getItem(KIND_KEY) || "user";
        if (!token) {
          setUser(false);
          return;
        }
        const meEndpoint = storedKind === "employee" ? "/employee-auth/me" : "/auth/me";
        try {
          const res = await api.get(meEndpoint);
          if (!active) return;
          setUser(res.data);
          setKind(storedKind);
        } catch {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(KIND_KEY);
          if (active) setUser(false);
        }
      } catch {
        if (active) {
          setCompanyAuthed(false);
          setUser(false);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshCompany = useCallback(async () => {
    try {
      const s = await api.get("/auth/google/session");
      const authed = !!s.data?.authenticated;
      setCompanyAuthed(authed);
      setCompanyEmail(s.data?.email || null);
      return authed;
    } catch {
      setCompanyAuthed(false);
      return false;
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(KIND_KEY, "user");
    setUser(data.user);
    setKind("user");
    return data.user;
  }, []);

  const loginEmployee = useCallback(async (username, pin) => {
    const { data } = await api.post("/employee-auth/login", { username, pin });
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(KIND_KEY, "employee");
    setUser(data.employee);
    setKind("employee");
    return data.employee;
  }, []);

  // Full logout — clears BOTH tiers and returns to the company gate.
  const logout = useCallback(async () => {
    try {
      await api.post("/auth/google/logout");
    } catch (e) {
      console.warn("company logout failed", e);
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(KIND_KEY);
    setUser(false);
    setKind(null);
    setCompanyAuthed(false);
    setCompanyEmail(null);
    window.location.replace("/");
  }, []);

  // Switch internal user — clears tier-2 only, keeps the company gate session.
  const switchUser = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(KIND_KEY);
    setUser(false);
    setKind(null);
  }, []);

  const refreshUser = useCallback((next) => setUser(next), []);

  const value = useMemo(
    () => ({
      user,
      kind,
      loading,
      companyAuthed,
      companyEmail,
      login,
      loginEmployee,
      logout,
      switchUser,
      refreshUser,
      refreshCompany,
    }),
    [user, kind, loading, companyAuthed, companyEmail, login, loginEmployee, logout, switchUser, refreshUser, refreshCompany],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
